import React, { useState } from 'react'

import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Divider, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { brainstormText } from 'content/brainstormText'
import isEqual from 'lodash/isEqual'
import { parseOverviewChallengeContent } from 'utils/parseOverviewChallengeContent'
import { validatePersonaInput } from 'utils/validatePersonaField'

import {
	SimonSaysPersonaExampleIdea,
	SimonSaysPersonaResponse,
	SimonSaysPromptResponse,
} from 'models/ideaModels'

import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import PromptCarouselComponent from 'pages/RoundRobin/components/Carousel'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import editIcon from 'assets/images/Pencil.svg'
import arrowDown from 'assets/images/arrowDown.svg'
import arrowUp from 'assets/images/arrowUp.svg'

import { styles } from './Section2.styles'

export const TEST_ID = 'simon-says-section2'

export type Props = {
	createPrompt: () => void
	currentResponses: string[]
	customer: string
	generateAiImage: () => Promise<void>
	generateImageDisabled: () => boolean
	imageAvailable: boolean
	imageData: string | null
	isAiImageLoading: boolean
	isCurrentResponseLoading: boolean
	loading: boolean
	manualChangeCustomerPrompts: (newCustomer: string) => void
	onRegenerateExampleIdeas: (
		personas: SimonSaysPersonaResponse[],
		personaIndex: number
	) => Promise<void>
	randomElement: string
	resetImage: () => void
	simonSaysPrompt?: SimonSaysPromptResponse
}

const Section2 = ({ simonSaysPrompt, ...props }: Props) => {
	const { showAlert } = useToastContext()
	const { showModal } = useModalContext()
	const [businessComparisonIndex, setBusinessComparisonIndex] = useState(0)
	const [showAIexampleIdea, setShowAIexampleIdea] = useState(false)
	const [temporalPromptsEditMode, setTemporalPrompts] = useState<string[]>([])

	// Edit state management for persona editing
	const [isEditMode, setIsEditMode] = useState(false)
	const [isRegeneratingExamples, setIsRegeneratingExamples] = useState(false)
	const [originalPersonas, setOriginalPersonas] = useState<
		SimonSaysPersonaResponse[]
	>([])
	const [editedPersonas, setEditedPersonas] = useState<
		SimonSaysPersonaResponse[]
	>([])

	const onClickRefresh = () => {
		props.createPrompt()
	}

	// Helper functions for state management operations
	const initializeEditState = (personas: SimonSaysPersonaResponse[]) => {
		setOriginalPersonas([...personas])
		setEditedPersonas([...personas])
	}

	const resetEditState = () => {
		setOriginalPersonas([])
		setEditedPersonas([])
	}

	const hasUnsavedChanges = () => {
		return (
			JSON.stringify(originalPersonas) !== JSON.stringify(editedPersonas)
		)
	}

	const restoreOriginalPersonas = () => {
		setEditedPersonas([...originalPersonas])
	}

	const cleanChallenges = (challenges: string[]): string[] =>
		challenges
			.map((challenge) => challenge.trim())
			.filter((challenge) => challenge.length > 0)

	const updatePersonaField = (
		personaIndex: number,
		overview: string,
		challenges: string[]
	): void => {
		setEditedPersonas((prevState) => {
			const cleanedChallenges = cleanChallenges(challenges)

			return prevState.map((persona, index) =>
				index === personaIndex
					? {
							...persona,
							overview: isEqual(persona.overview, overview)
								? persona.overview
								: overview,
							challenges: isEqual(
								persona.challenges,
								cleanedChallenges
							)
								? persona.challenges
								: cleanedChallenges,
						}
					: persona
			)
		})
	}

	// Ensure proper data mapping between display and edit formats
	const getDisplayPersonaData = () => {
		return isEditMode
			? editedPersonas
			: simonSaysPrompt?.data?.personas || []
	}

	// Legacy support for existing carousel integration
	const setPromptValue = (value: string) => {
		const newTemporalPrompts = [...temporalPromptsEditMode]
		newTemporalPrompts[businessComparisonIndex] = value
		setTemporalPrompts(newTemporalPrompts)

		const { overview, challenges } = parseOverviewChallengeContent(value)
		updatePersonaField(businessComparisonIndex, overview, challenges)
	}

	const currentPrompt = simonSaysPrompt?.data?.personas
		? simonSaysPrompt.data.personas[businessComparisonIndex]
		: undefined

	// Get the appropriate persona data based on edit mode
	const displayPersonas = getDisplayPersonaData()

	// Format persona content for display and editing with proper structure
	const currentPrompts = displayPersonas.map(
		(persona) =>
			persona.overview +
			'\n\n' +
			persona.challenges
				.map((challenge, i) => `${i + 1}. ${challenge}`)
				.join('\n')
	)

	// Create names array using persona role and name information
	const personaNames = displayPersonas.map(
		(persona) => `${persona.role} - ${persona.name}`
	)

	const onClickEdit = () => {
		if (simonSaysPrompt?.data?.personas) {
			initializeEditState(simonSaysPrompt.data.personas)
			setIsEditMode(true)
			// Initialize temporal prompts with current persona data for editing
			const editPrompts = simonSaysPrompt.data.personas.map(
				(persona) =>
					persona.overview +
					'\n\n' +
					persona.challenges
						.map((challenge, i) => `${i + 1}. ${challenge}`)
						.join('\n')
			)
			setTemporalPrompts(editPrompts)
		}
	}

	const onClickCancelEdit = () => {
		if (hasUnsavedChanges()) {
			showModal('WARNING_MODAL', {
				onProceed: () => {
					restoreOriginalPersonas()
					setIsEditMode(false)
					resetEditState()
				},
				onCancel: () => {
					// User chose to keep editing, do nothing
				},
			})
		} else {
			setIsEditMode(false)
			resetEditState()
		}
	}

	const onClickSaveEdit = async () => {
		if (editedPersonas.length > 0) {
			const persona = editedPersonas[businessComparisonIndex]
			const { overview, challenges } = persona

			const validation = validatePersonaInput(overview, challenges)
			if (!validation.isValid && validation.error) {
				if (showAlert) {
					showAlert({
						objectType: 'defaultError',
						severity: ToastSeverity.ERROR,
						bodyText: validation.error,
					})
				}

				return
			}

			setIsRegeneratingExamples(true)
			await props.onRegenerateExampleIdeas(
				editedPersonas,
				businessComparisonIndex
			)
			setIsRegeneratingExamples(false)
		}
		setShowAIexampleIdea(false)
		setIsEditMode(false)
		resetEditState()
		if (showAlert) {
			showAlert({
				objectType: 'editPrompt',
				severity: ToastSeverity.SUCCESS,
			})
		}
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box
					display="flex"
					flexDirection="column"
					gap="2rem"
					flexGrow="1"
					paddingY={{ xs: '0', sm: '1rem' }}
				>
					<Box
						display={'flex'}
						flexDirection={{
							xs: 'column',
							sm: 'column',
						}}
						justifyContent={'space-between'}
						gap={'1rem'}
					>
						<Box
							display={'flex'}
							justifyContent={'space-between'}
							flexDirection={{
								xs: 'column',
								sm: 'row',
							}}
							alignItems={{
								xs: 'flex-start',
								sm: 'center',
							}}
							gap={'1rem'}
							padding={0}
						>
							<Typography
								data-testid={`${TEST_ID}__intro-text`}
								color="black"
								sx={{ opacity: 0.87 }}
								fontSize={{
									xs: 16,
									sm: 20,
								}}
								gap={'0.5rem'}
								maxWidth={{
									sm: '488px',
									md: '600px',
									lg: '940px',
								}}
							>
								To inform your ideation, consider the following
								perspectives different customer personas might
								have as it relates to your innovation topic.
							</Typography>
							<Box
								display={'inline-flex'}
								flexDirection={'row'}
								justifyContent={'flex-end'}
								alignItems={'center'}
								gap={'0.5rem'}
								padding={'6px 8px'}
							>
								<ButtonWithIconComponent
									sx={{ padding: 0 }}
									disabled={props.loading}
									Icon={<RefreshIcon fontSize="small" />}
									text="REFRESH"
									data-testid={`${TEST_ID}__refresh-button`}
									onClickAction={onClickRefresh}
									iconWidth={'13px'}
								/>
							</Box>
						</Box>
					</Box>
					<Box marginLeft="2rem" marginRight="2rem">
						{props.loading ? (
							<LoadingSpinner />
						) : (
							<>
								<Box sx={styles.carouselWrapper}>
									<Box
										flexDirection={{
											xs: 'column',
											sm: 'row',
										}}
										className="button-wrapper"
									>
										{simonSaysPrompt?.data?.personas &&
											simonSaysPrompt.data.personas.map(
												(persona, index) => (
													<>
														<Button
															key={persona.name}
															data-testid={`${TEST_ID}__business-comparisons-${persona.name}`}
															className={
																index ===
																businessComparisonIndex
																	? 'selected'
																	: ''
															}
															onClick={() =>
																setBusinessComparisonIndex(
																	index
																)
															}
															disabled={
																isEditMode
															}
														>
															PERSONA {index + 1}
														</Button>
														{index <
															simonSaysPrompt.data
																.personas
																.length -
																1 && (
															<Divider
																orientation="vertical"
																variant="middle"
																flexItem={true}
															/>
														)}
													</>
												)
											)}
									</Box>
								</Box>
								<Box
									data-testid={`${TEST_ID}__business-comparison-traits`}
								>
									{simonSaysPrompt && (
										<PromptCarouselComponent
											disabledArrows={isEditMode}
											setCurrentIndex={
												setBusinessComparisonIndex
											}
											currentIndex={
												businessComparisonIndex
											}
											currentPrompts={currentPrompts}
											promptsEditMode={
												temporalPromptsEditMode
											}
											editable={isEditMode}
											setPromptValue={setPromptValue}
											names={personaNames}
											sxCardOverrides={{
												backgroundColor:
													'rgba(237, 231, 246, 0.8)',
											}}
										/>
									)}
								</Box>
							</>
						)}
					</Box>
					{isEditMode ? (
						<Box
							display="flex"
							flexDirection={{
								xs: 'column',
								sm: 'row',
							}}
							gap="2rem"
						>
							<Button
								color="secondary"
								variant="contained"
								data-testid={`${TEST_ID}__edit-save-button`}
								onClick={onClickSaveEdit}
								disabled={isRegeneratingExamples}
							>
								<Typography>SAVE CHANGES</Typography>
							</Button>
							<Button
								variant="text"
								color="secondary"
								data-testid={`${TEST_ID}__edit-cancel-button`}
								onClick={onClickCancelEdit}
							>
								<Typography>CANCEL</Typography>
							</Button>
						</Box>
					) : (
						<Box
							display={'flex'}
							alignItems={'center'}
							justifyContent={'end'}
						>
							<ButtonWithIconComponent
								sx={{ padding: 0 }}
								disabled={props.loading}
								iconSvg={editIcon}
								text="EDIT"
								data-testid={`${TEST_ID}__edit-button`}
								onClickAction={onClickEdit}
							/>
						</Box>
					)}
					{!!props.currentResponses && (
						<>
							<Typography
								color="black"
								sx={{ opacity: 0.87 }}
								fontSize={{
									xs: 16,
									sm: 20,
								}}
							>
								{brainstormText.global.ideasDropdown}
							</Typography>
							<Box>
								<Box borderBottom={2} borderColor={'#D6D4FF'}>
									<Button
										fullWidth
										color="inherit"
										data-testid={`${TEST_ID}-show-ai-example-button`}
										onClick={() =>
											setShowAIexampleIdea(
												!showAIexampleIdea
											)
										}
									>
										<Box
											display={'flex'}
											justifyContent="space-between"
											width="100%"
										>
											<Typography
												color="black"
												sx={{ opacity: 0.87 }}
												fontSize={12}
												lineHeight={'266%'}
											>
												View AI-Generated Example Ideas
											</Typography>
											<img
												alt="arrow"
												data-testid={`${TEST_ID}-arrow`}
												src={
													(showAIexampleIdea
														? arrowUp
														: arrowDown) as unknown as string
												}
											/>
										</Box>
									</Button>
								</Box>
								{showAIexampleIdea && (
									<Box
										bgcolor={'rgba(237, 231, 246, 0.8)'}
										padding={'1rem'}
									>
										{props.isCurrentResponseLoading && (
											<LoadingSpinner />
										)}
										<Typography
											fontSize={16}
											color="black"
											component="span"
										>
											{`${currentPrompt?.role} - ${currentPrompt?.name}`}
										</Typography>

										<ul data-testid="gen-ai-response-list">
											{currentPrompt?.exampleIdeas &&
												currentPrompt.exampleIdeas.map(
													(
														el: SimonSaysPersonaExampleIdea,
														i: number
													) => (
														<>
															<Typography
																fontSize={16}
																color="black"
																component="li"
																key={`ai-solution-${i}`}
																sx={{
																	opacity: 0.87,
																}}
																variant="body1"
																style={{
																	margin: '10px 0 10px 0',
																}}
															>
																{el.topic}
															</Typography>
															<ul>
																{el.subpoints.map(
																	(
																		subpoint: string,
																		index: number
																	) => (
																		<Typography
																			fontSize={
																				16
																			}
																			color="black"
																			component="li"
																			key={`ai-solution-${i}-${index}`}
																		>
																			{
																				subpoint
																			}
																		</Typography>
																	)
																)}
															</ul>
														</>
													)
												)}
										</ul>
									</Box>
								)}
							</Box>
						</>
					)}
				</Box>
			</Box>
		</>
	)
}
export default Section2
